import { defineStore } from 'pinia';
import { type Component, isRef, markRaw, type Ref, type ComputedRef } from 'vue';
import type { ContextSwitcherEntry } from '@lib/composables/useContextSwitcher';
import { getContrastColor } from '@lib/composables/useContrastColor';
import { BOOTSTRAP_COLORS, THEME_COLORS } from '@lib/utils/bootstrapColors';

type DynStr = string | Ref<string> | ComputedRef<string> | (() => string);

export type HeaderAction = {
	icon?: () => string;
	btnClass?: () => string;
	title?: () => string;
	onClick?: () => void;
	id?: string;
	component?: Component;
};

export type HeaderActionHandle = {
	remove: () => void;
	update?: (
		next: Partial<{ icon: DynStr; btnClass: DynStr; title: DynStr; onClick: () => void; id: string }>,
	) => void;
};

function toGetter(v: DynStr): () => string {
	if (typeof v === 'function') return () => (v as () => string)();
	if (isRef(v)) return () => v.value;
	return () => v;
}

type ActionOptions = {
	icon?: DynStr;
	btnClass?: DynStr;
	title?: DynStr;
	onClick?: () => void;
	id?: string;
	component?: Component;
};

/** Adds an action to one of the navbar action lists and hands back a handle to it. */
function addAction(list: HeaderAction[], opts: ActionOptions): HeaderActionHandle {
	const { icon, btnClass, title, onClick, id, component } = opts;
	// If an action with same onClick or id exists, reuse that one and still return a handle for it
	let existing = list.find((a) => (onClick && a.onClick === onClick) || (id && a.id === id));
	if (!existing) {
		existing = {
			icon: icon ? toGetter(icon) : undefined,
			btnClass: btnClass ? toGetter(btnClass) : undefined,
			title: title ? toGetter(title) : undefined,
			onClick,
			id,
			component: component ? markRaw(component) : undefined,
		};
		list.push(existing);
	}

	const remove = () => {
		const idx = list.indexOf(existing!);
		if (idx !== -1) list.splice(idx, 1);
	};

	const update = (
		next: Partial<{ icon: DynStr; btnClass: DynStr; title: DynStr; onClick: () => void; id: string }>,
	) => {
		if (next.icon !== undefined) existing!.icon = toGetter(next.icon);
		if (next.btnClass !== undefined) existing!.btnClass = toGetter(next.btnClass);
		if (next.title !== undefined) existing!.title = toGetter(next.title);
		if (next.onClick !== undefined) existing!.onClick = next.onClick;
		if (next.id !== undefined) existing!.id = next.id;
	};

	return { remove, update };
}

/** localStorage key the language the user picked is kept under. */
export const APP_LOCALE_STORAGE_KEY = 'app.locale';

/** The language the user picked on an earlier visit, null when they never picked one. */
function loadAppLocale(): string | null {
	try {
		return globalThis.localStorage?.getItem(APP_LOCALE_STORAGE_KEY) || null;
	} catch {
		return null;
	}
}

export const useAppOptionsStore = defineStore({
	id: 'appOption',
	state: () => ({
		title: globalThis.config?.['app.name'] || globalThis.env?.app?.name || 'EF Vue SPA App',
		title_short: globalThis.config?.['app.short_name'],
		backgroundUrlExtern: globalThis.config?.['app.background_url_extern'] ?? '/img/login-background.jpg',

		is_minimized: false,

		appTheme: 'light' as 'light' | 'dark' | 'auto',
		appThemeDividers: false,
		appThemeGradients: true,
		appThemeShadows: false,

		/** The language the user picked, kept across reloads; null follows the browser, see plugins/i18n. */
		appLocale: loadAppLocale(),

		appPrimaryColor: globalThis.config?.['app.primary_color'] ?? THEME_COLORS.primary,
		appSecondaryColor: globalThis.config?.['app.secondary_color'] ?? THEME_COLORS.secondary,

		/** The app's own palette for its colour fields, null for the lib's (colorPalette()). */
		colorPresets: null as string[] | null,

		showApplications: globalThis.config?.['app.show_applications'] ?? '1',

		appSidebarCollapsed: false,
		appSidebarMinified: false,
		appSidebarSearch: false,
		appFooter: true,

		is_locked: false,

		// Set while a context switch is in flight, see the context plugin: the main layout puts a
		// spinner where the router view is, so no page renders or loads against the context that
		// is on its way out.
		is_context_switching: false,

		search: undefined as
			| undefined
			| {
					callback?: (s: string) => boolean;
					component?: Component;
					displayBreakpoint?: string;
			  },

		headerActions: [] as HeaderAction[],

		// Actions shown next to the context switchers, for app wide selections that scope the
		// data of every page, e.g. a year picker.
		contextActions: [] as HeaderAction[],

		routerKey: Date.now().toString(),

		// We allow the override of Layout Components, e.g. the Context Menu to visualise App specific data
		contextHeaderSlot: null as null | Component,

		// Places of the app offered in a context's dropdown above its records, see useContextSwitcher
		contextEntries: [] as ContextSwitcherEntry[],
	}),
	actions: {
		refreshRouterView() {
			this.routerKey = Date.now().toString();
		},
		/** Picks the language for good, over the browser's. */
		setAppLocale(locale: string) {
			this.appLocale = locale;
			try {
				globalThis.localStorage?.setItem(APP_LOCALE_STORAGE_KEY, locale);
			} catch {
				// Storage blocked: the pick holds until the next reload.
			}
		},
		/**
		 * Takes a public app setting just saved in the settings, so the page shows it without a
		 * reload. The state the layout reads from it follows; any other key waits for one.
		 */
		applyConfig(key: string, value: string | null) {
			if (globalThis.config) globalThis.config[key] = value ?? '';

			switch (key) {
				case 'app.name':
					this.title = value || globalThis.env?.app?.name || 'EF Vue SPA App';
					break;
				case 'app.short_name':
					this.title_short = value ?? '';
					break;
				case 'app.primary_color':
					this.appPrimaryColor = value || THEME_COLORS.primary;
					break;
				case 'app.secondary_color':
					this.appSecondaryColor = value || THEME_COLORS.secondary;
					break;
				case 'app.show_applications':
					this.showApplications = value ?? '1';
					break;
			}
		},
		toggleIsMinimized() {
			this.is_minimized = !this.is_minimized;
		},
		addHeaderAction(opts: ActionOptions): HeaderActionHandle {
			return addAction(this.headerActions, opts);
		},
		addContextAction(opts: ActionOptions): HeaderActionHandle {
			return addAction(this.contextActions, opts);
		},
		clearHeaderActions() {
			this.headerActions = [] as HeaderAction[];
		},
		clearContextActions() {
			this.contextActions = [] as HeaderAction[];
		},
		/*init() {
			this.title = window.config?.['app.name'] || 'FetSy 2.0';
			this.title_short = window.config?.['app.name.short'] || this.title;
			this.loginBackgroundUrl = window.config?.['app.login.background'] || '/img/login-background.jpg';
			this.registerBackgroundUrl = window.config?.['app.register.background'] || '/img/login-background.jpg';
		},*/
	},
});

export function hexToRgbList(hex: string) {
	const h = hex.replace('#', '');
	const [r, g, b] =
		h.length === 3 ?
			[...h].map((c) => parseInt(c + c, 16))
		:	[h.slice(0, 2), h.slice(2, 4), h.slice(4, 6)].map((c) => parseInt(c, 16));
	return `${r}, ${g}, ${b}`;
}

/**
 * The text colour that reads better on a fill of the given colour, white or the dark ink. The
 * primary is set at runtime by an admin, so a light one must not leave its buttons unreadable.
 * The rule is getContrastColor(), the same for every place that sets text on a colour.
 */
export function readableOn(hex: string): string {
	return getContrastColor(hex);
}

const c = BOOTSTRAP_COLORS;

/** Bootstrap's own colours, its $colors map. */
const COLOR_FIELD_BOOTSTRAP = [
	c.blue, c.indigo, c.purple, c.pink, c.red, c.orange, c.yellow, c.green, c.teal, c.cyan,
	c.gray600, c.gray800, c.black, c.white,
];

/** The palette of Laravel's old front-end preset, which the first apps took their colours from. */
const PRESET_COLORS = [c.green, c.red, c.yellow, c.gray500, c.cyan, c.blue, c.gray600, c.white];

/** A colour mixed with white, Bootstrap's tint-color(): a weight of 0.6 is its 200 shade. */
export function tint(hex: string, weight = 0.6): string {
	const channels = hexToRgbList(hex).split(', ').map(Number);

	return '#' + channels.map((c) => Math.round(c + (255 - c) * weight).toString(16).padStart(2, '0')).join('');
}

/**
 * What a colour field (efColorInput) offers beside its picker, each colour followed by its light
 * version: the app's primary, Bootstrap's colours and those of Laravel's old preset, each once.
 * EfLibOptions.colorPresets replaces it.
 */
export function colorPalette(primary: string): string[] {
	const colors = [primary, ...COLOR_FIELD_BOOTSTRAP, ...PRESET_COLORS].map((color) => color.toLowerCase());

	return [...new Set(colors.flatMap((color) => [color, tint(color)]))];
}
